// Reverb, drawn: the shape of the tail it makes — the pre-delay gap, the
// early reflections the room's size spreads, the decay to silence — over
// the knobs that move it: pre-delay, decay, size, stereo image, the input
// filter, the high-shelf damping in the tail, Freeze and Dry/Wet.

import { useMemo } from "react";
import { clamp, fromNorm, normOf, REVERB_SIZE_MAX, REVERB_SIZE_MIN, reverbModel, writeDb, writeHz, writeMs, writeRoomSize } from "@web-shared/lib/device-params";
import { fmtDb, fmtHz, fmtMs, fmtPct, Knob, Toggle } from "@web-shared/components/devices/controls";
import type { EditorProps } from "@web-shared/components/devices/types";

const W = 300;
const H = 56;

export function ReverbEditor({ device, disabled, set }: EditorProps) {
  const r = useMemo(() => reverbModel(device), [device]);
  const param = (i: number | undefined) => (i === undefined ? undefined : device.params[i]);
  const toggle = (i: number | undefined, on: boolean) => {
    const p = param(i);
    if (p && i !== undefined) set(i, on ? p.max : p.min, true);
  };
  const knob = (i: number | undefined, label: string, value: number, min: number, max: number, format: (v: number) => string, write: (v: number) => number, opts: { log?: boolean; bipolar?: boolean; off?: boolean } = {}) =>
    i === undefined ? null : (
      <Knob label={label} value={value} min={min} max={max} log={opts.log} bipolar={opts.bipolar} format={format} disabled={disabled || opts.off} onDraft={(v) => set(i, write(v), false)} onCommit={(v) => set(i, write(v), true)} />
    );

  // The tail, drawn over a window long enough to show it end.
  const shape = useMemo(() => {
    const total = (r.predelayMs + (r.freeze ? 12000 : r.decayMs)) * 1.05;
    const x = (ms: number) => (ms / total) * W;
    const pre = x(r.predelayMs);
    const er = x(5 + r.size * 80);
    const pts: string[] = [`${pre.toFixed(1)},${H - 2}`];
    for (let i = 0; i <= 60; i++) {
      const t = i / 60;
      const ms = r.predelayMs + t * (r.freeze ? 12000 : r.decayMs);
      const env = r.freeze ? 1 : Math.pow(10, (-60 * t) / 20);
      const level = Math.sqrt(env) * Math.min(1, 0.4 + 0.6 * Math.pow(10, r.diffuseDb / 20));
      pts.push(`${x(ms).toFixed(1)},${(H - 2 - level * (H - 10)).toFixed(1)}`);
    }
    pts.push(`${x(r.predelayMs + (r.freeze ? 12000 : r.decayMs)).toFixed(1)},${H - 2}`);
    const taps = Array.from({ length: 6 + Math.round(r.size * 10) }, (_, i) => {
      const at = pre + ((i * 0.618) % 1) * er;
      return { x: at, h: (H - 10) * clamp(Math.pow(10, r.reflectDb / 20), 0, 1.5) * (0.9 - (0.5 * (at - pre)) / Math.max(1, er)) };
    });
    return { area: pts.join(" "), taps, pre };
  }, [r]);

  const mixP = param(r.idx.mix);
  const widthP = param(r.idx.width);
  const sizeP = param(r.idx.size);
  const predelayP = param(r.idx.predelay);
  const decayP = param(r.idx.decay);
  const inFreqP = param(r.idx.inFreq);
  const inWidthP = param(r.idx.inWidth);
  const hsFreqP = param(r.idx.hiShelfFreq);
  const hsGainP = param(r.idx.hiShelfGain);
  const reflectP = param(r.idx.reflect);
  const diffuseP = param(r.idx.diffuse);
  const hsOn = r.damping > 0 || (r.idx.hiShelfOn !== undefined && (param(r.idx.hiShelfOn)!.value > (param(r.idx.hiShelfOn)!.min + param(r.idx.hiShelfOn)!.max) / 2));

  return (
    <div className="flex flex-col gap-2" data-testid="reverb">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-[56px] w-full min-w-[300px] rounded bg-[var(--bg-deep)]" preserveAspectRatio="none" role="img" aria-label="Reverb tail">
        <polygon points={shape.area} fill="var(--solo)" opacity={0.22} />
        <polyline points={shape.area} fill="none" stroke="var(--solo)" strokeWidth={1.2} />
        {shape.taps.map((t, i) => (
          <line key={i} x1={t.x} x2={t.x} y1={H - 2} y2={H - 2 - Math.max(2, t.h)} stroke="var(--foreground)" opacity={0.55} />
        ))}
        {shape.pre > 4 && (
          <text x={2} y={10} fontSize={8} fill="var(--muted-foreground)">
            {fmtMs(r.predelayMs)}
          </text>
        )}
        <text x={W - 3} y={10} fontSize={8} textAnchor="end" fill="var(--muted-foreground)">
          {r.freeze ? "frozen" : fmtMs(r.decayMs)}
        </text>
      </svg>
      <div className="flex items-end gap-1">
        {predelayP && knob(r.idx.predelay, "Predelay", r.predelayMs, 0.5, 250, fmtMs, (v) => writeMs(predelayP, v, 0.5, 250), { log: true })}
        {decayP && knob(r.idx.decay, "Decay", r.decayMs, 200, 60000, fmtMs, (v) => writeMs(decayP, v, 200, 60000), { log: true, off: r.freeze })}
        {sizeP && knob(r.idx.size, "Size", r.roomSize, REVERB_SIZE_MIN, REVERB_SIZE_MAX, (v) => v.toFixed(v < 10 ? 2 : 0), (v) => writeRoomSize(sizeP, v), { log: true })}
        {widthP && knob(r.idx.width, "Stereo", r.width, 0, 1, (t) => `${Math.round(t * 120)}°`, (t) => (widthP.max > 1.0001 ? clamp(t * 120, widthP.min, widthP.max) : fromNorm(widthP, t)))}
        {mixP && knob(r.idx.mix, "Dry/Wet", normOf(mixP, 0.3), 0, 1, fmtPct, (t) => fromNorm(mixP, t))}
      </div>
      <div className="flex items-end gap-1">
        {(r.idx.lowCutOn !== undefined || r.idx.highCutOn !== undefined) && (
          <div className="mb-4 flex flex-col gap-1">
            {r.idx.lowCutOn !== undefined && <Toggle label="Lo Cut" on={r.lowCut !== null} disabled={disabled} onChange={(on) => toggle(r.idx.lowCutOn, on)} title="Cut the lows going into the room" />}
            {r.idx.highCutOn !== undefined && <Toggle label="Hi Cut" on={r.highCut !== null} disabled={disabled} onChange={(on) => toggle(r.idx.highCutOn, on)} title="Cut the highs going into the room" />}
          </div>
        )}
        {inFreqP && knob(r.idx.inFreq, "In Freq", r.inFreq, 50, 18000, fmtHz, (v) => writeHz(inFreqP, v, 50, 18000), { log: true })}
        {inWidthP && knob(r.idx.inWidth, "In Width", inWidthP.value, inWidthP.min, inWidthP.max, (v) => v.toFixed(1), (v) => v)}
        {(r.idx.hiShelfOn !== undefined || r.idx.freeze !== undefined) && (
          <div className="mb-4 flex flex-col gap-1">
            {r.idx.hiShelfOn !== undefined && <Toggle label="Damp" on={hsOn} disabled={disabled} onChange={(on) => toggle(r.idx.hiShelfOn, on)} title="High shelf in the tail: highs die away faster" />}
            {r.idx.freeze !== undefined && <Toggle label="Freeze" on={r.freeze} disabled={disabled} onChange={(on) => toggle(r.idx.freeze, on)} title="Hold the tail forever" />}
          </div>
        )}
        {hsFreqP && knob(r.idx.hiShelfFreq, "Damp Freq", r.dampFreq, 20, 16000, fmtHz, (v) => writeHz(hsFreqP, v, 20, 16000), { log: true, off: !hsOn })}
        {hsGainP && knob(r.idx.hiShelfGain, "Damp Gain", hsGainP.value, hsGainP.min, hsGainP.max, (v) => (hsGainP.min < 0 ? fmtDb(v) : v.toFixed(2)), (v) => v, { off: !hsOn })}
        {reflectP && knob(r.idx.reflect, "Reflect", r.reflectDb, -30, 6, fmtDb, (v) => writeDb(reflectP, v), { bipolar: true })}
        {diffuseP && knob(r.idx.diffuse, "Diffuse", r.diffuseDb, -30, 6, fmtDb, (v) => writeDb(diffuseP, v), { bipolar: true })}
      </div>
    </div>
  );
}
